import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Keyboard, Layers, MapPin, Scale } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from 'utils/classnames';
import { DISPOSITION_COLORS, SCREEN_TYPES } from 'utils/constants';
import { useAppStore } from 'store';
import { Button } from 'components/ui/Button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { useKeyboardInputToggle } from 'hooks/useKeyboardInputToggle';
import { usePalletBuildStore } from 'store/palletBuildStore';
import { closePallet, movePallet } from 'services/api/palletBuild';
import { playErrorSound, playGoodSound } from 'utils/sounds';

type Step = 'pallet' | 'weight' | 'location';

export const WeighClosePage = () => {
  const { pallet_id, pallet, setPalletId, reset } = usePalletBuildStore();
  const [palletScan, setPalletScan] = useState(pallet_id ?? '');
  const [weight, setWeight] = useState('');
  const [locationScan, setLocationScan] = useState('');
  const [locationId, setLocationId] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const palletRef = useRef<HTMLInputElement>(null);
  const weightRef = useRef<HTMLInputElement>(null);
  const locationRef = useRef<HTMLInputElement>(null);
  const { keyboardVisible: palletKeyboard, toggleKeyboardInput: togglePalletKeyboard } =
    useKeyboardInputToggle(palletRef);
  const { keyboardVisible: weightKeyboard, toggleKeyboardInput: toggleWeightKeyboard } =
    useKeyboardInputToggle(weightRef);
  const { keyboardVisible: locationKeyboard, toggleKeyboardInput: toggleLocationKeyboard } =
    useKeyboardInputToggle(locationRef);
  const { setCurrentScreen } = useAppStore();

  const [step, setStep] = useState<Step>('pallet');

  const activePalletId = pallet_id ?? palletScan.trim();
  const isPalletClosed = pallet?.status === 'closed';

  // Focusing here (rather than right after setStep) matters: setStep is async, so calling
  // .focus() in the same handler still sees the input as disabled from the prior render.
  useEffect(() => {
    if (step === 'weight') weightRef.current?.focus();
    if (step === 'location') locationRef.current?.focus();
  }, [step]);

  const handlePalletKeyDown = async (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (palletScan.trim()) {
        setHasError(false);
        toast('Scanning pallet...');
        try {
          await setPalletId(palletScan.trim());
          const scannedPallet = usePalletBuildStore.getState().pallet;
          if (scannedPallet?.weight_kg) setWeight(String(scannedPallet.weight_kg));
          if (scannedPallet?.location_id) setLocationId(scannedPallet.location_id);
          toast.success('Pallet scanned');
          playGoodSound();
          setStep('weight');
        } catch {
          setErrorMessage('Failed to scan pallet. Please try again.');
          setHasError(true);
          playErrorSound();
        }
      }
    }
  };

  const handleWeightKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isPalletClosed) return;
      const parsed = parseFloat(weight);
      if (!weight.trim() || Number.isNaN(parsed) || parsed <= 0) {
        toast.error('Enter a valid weight.');
        playErrorSound();
        return;
      }
      setStep('location');
    }
  };

  const handleLocationKeyDown = async (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const trimmed = locationScan.trim();
      if (!trimmed) return;
      setHasError(false);
      if (!activePalletId) {
        setLocationScan('');
        setErrorMessage('Scan a pallet first.');
        setHasError(true);
        requestAnimationFrame(() => locationRef.current?.focus());
        return;
      }
      toast('Moving pallet...');
      try {
        const data = await movePallet(activePalletId, trimmed);
        if (data.success) {
          setLocationId(trimmed);
          toast.success('Pallet moved to location');
          playGoodSound();
        } else {
          setLocationScan('');
          setErrorMessage(data.message || "Location doesn't exist. Please scan again.");
          setHasError(true);
          playErrorSound();
          requestAnimationFrame(() => locationRef.current?.focus());
        }
      } catch {
        setLocationScan('');
        setErrorMessage("Location doesn't exist. Please scan again.");
        setHasError(true);
        playErrorSound();
        requestAnimationFrame(() => locationRef.current?.focus());
      }
    }
  };

  const handleClose = async () => {
    if (!activePalletId || !weight.trim() || !locationId) return;
    setHasError(false);
    toast('Closing pallet...');
    try {
      const data = await closePallet(activePalletId, parseFloat(weight), locationId);
      if (!data.success) {
        setErrorMessage('Failed to close pallet. Please try again.');
        setHasError(true);
        playErrorSound();
        return;
      }
      reset();
      toast.success('Pallet closed successfully');
      playGoodSound();
      setCurrentScreen(SCREEN_TYPES.PALLET_BUILD);
    } catch {
      setErrorMessage('Failed to close pallet. Please try again.');
      setHasError(true);
      playErrorSound();
    }
  };

  const onCancel = () => {
    setCurrentScreen(SCREEN_TYPES.PALLET_BUILD);
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-4 py-4',
        (hasError || isPalletClosed) &&
          `-mx-4 -mb-6 min-h-full px-6 pb-6 pt-4 ${DISPOSITION_COLORS.PROBLEM.gradient}`
      )}
    >
      <div className="flex flex-col items-center gap-1">
        <span
          className={cn(
            'text-xs font-semibold uppercase tracking-widest',
            hasError || isPalletClosed ? 'text-white/80' : 'text-emerald-500'
          )}
        >
          Pallet Build · Step 3
        </span>
        <h2
          className={cn(
            'text-2xl font-bold',
            hasError || isPalletClosed ? 'text-white' : 'text-slate-800'
          )}
        >
          Weigh & Close
        </h2>
        {isPalletClosed ? (
          <p className="text-3xl font-bold text-white text-center">
            Pallet is closed — weight cannot be changed
          </p>
        ) : (
          hasError && <p className="text-3xl font-bold text-white text-center">{errorMessage}</p>
        )}
      </div>

      <div className="flex items-center gap-3 rounded-xl bg-emerald-50 px-4 py-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100">
          <Scale className="h-4 w-4 text-emerald-600" />
        </div>
        <div className="flex flex-col">
          <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">
            Close pallet to staged
          </span>
          <span className="text-sm text-slate-400">Record weight and close the pallet</span>
        </div>
      </div>

      {step === 'pallet' ? (
        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
          <Label className="mb-2 block text-sm font-semibold text-slate-600">Pallet Barcode</Label>
          <div className="flex items-center gap-2">
            <Input
              ref={palletRef}
              type="text"
              inputMode={palletKeyboard ? 'text' : 'none'}
              placeholder="Scan pallet ID..."
              value={palletScan}
              onChange={(e) => setPalletScan(e.target.value)}
              onKeyDown={handlePalletKeyDown}
              onBlur={() => requestAnimationFrame(() => palletRef.current?.focus())}
              autoFocus
              autoComplete="off"
              spellCheck={false}
              className="text-base"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={togglePalletKeyboard}
              aria-pressed={palletKeyboard}
              className="h-12 w-12 shrink-0 rounded-xl border-slate-300"
            >
              <Keyboard className="h-5 w-5" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-3">
          <Layers className="h-4 w-4 text-emerald-500 shrink-0" />
          <div className="flex flex-col min-w-0">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Pallet
            </span>
            <span className="font-mono text-sm font-bold text-slate-800 truncate">
              {activePalletId}
            </span>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <Label className="mb-2 block text-sm font-semibold text-slate-600">
          Weight <span className="font-normal text-slate-400">(kg)</span>
        </Label>
        <div className="flex items-center gap-2">
          <Input
            ref={weightRef}
            type="number"
            inputMode={weightKeyboard ? 'decimal' : 'none'}
            placeholder="Enter weight..."
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            onKeyDown={handleWeightKeyDown}
            onBlur={() =>
              step === 'weight' && requestAnimationFrame(() => weightRef.current?.focus())
            }
            disabled={step === 'pallet' || isPalletClosed}
            autoComplete="off"
            className="text-base"
            min="0"
            step="0.1"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={toggleWeightKeyboard}
            aria-pressed={weightKeyboard}
            disabled={step === 'pallet' || isPalletClosed}
            className="h-12 w-12 shrink-0 rounded-xl border-slate-300"
          >
            <Keyboard className="h-5 w-5" />
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <Label className="mb-2 block text-sm font-semibold text-slate-600">Location</Label>
        {locationId ? (
          <div className="flex items-center gap-3 rounded-xl bg-slate-50 px-4 py-3">
            <MapPin className="h-4 w-4 text-emerald-500 shrink-0" />
            <div className="flex flex-col min-w-0">
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                Location
              </span>
              <span className="font-mono text-sm font-bold text-slate-800 truncate">
                {locationId}
              </span>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Input
              ref={locationRef}
              type="text"
              inputMode={locationKeyboard ? 'text' : 'none'}
              placeholder="Scan location..."
              value={locationScan}
              onChange={(e) => setLocationScan(e.target.value)}
              onKeyDown={handleLocationKeyDown}
              onBlur={() =>
                step === 'location' && requestAnimationFrame(() => locationRef.current?.focus())
              }
              disabled={step !== 'location'}
              autoComplete="off"
              spellCheck={false}
              className="text-base"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={toggleLocationKeyboard}
              aria-pressed={locationKeyboard}
              disabled={step !== 'location'}
              className="h-12 w-12 shrink-0 rounded-xl border-slate-300"
            >
              <Keyboard className="h-5 w-5" />
            </Button>
          </div>
        )}
      </div>

      <div className="buttons-container !bg-transparent">
        <Button
          onClick={handleClose}
          disabled={!activePalletId || !weight.trim() || !locationId || isPalletClosed}
          className="flex-1 h-12 bg-emerald-600 text-base hover:bg-emerald-700"
        >
          Close Pallet
        </Button>
        <Button onClick={onCancel} variant="secondary" className="flex-1 h-12">
          Back
        </Button>
      </div>
    </div>
  );
};
